import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuthStore from '../store/useAuthStore';
import api from '../services/api';

const GCC_LOCATIONS = [
  { country: 'Kuwait', cities: ['Kuwait City', 'Salmiya', 'Hawalli', 'Farwaniya', 'Ahmadi', 'Jahra'] },
  { country: 'Saudi Arabia', cities: ['Riyadh', 'Jeddah', 'Dammam', 'Khobar', 'Mecca', 'Medina'] },
  { country: 'UAE', cities: ['Dubai', 'Abu Dhabi', 'Sharjah', 'Ajman', 'RAK', 'Fujairah'] },
  { country: 'Qatar', cities: ['Doha', 'Al Wakrah', 'Al Khor', 'Lusail'] },
  { country: 'Bahrain', cities: ['Manama', 'Riffa', 'Muharraq', 'Hamad Town'] },
  { country: 'Oman', cities: ['Muscat', 'Salalah', 'Sohar', 'Nizwa'] },
];

const INTEREST_OPTIONS = [
  { value: 'Photography', icon: '📸' },
  { value: 'Videography', icon: '🎬' },
  { value: 'Graphic Design', icon: '🎨' },
  { value: 'Social Media', icon: '📱' },
  { value: 'Web Development', icon: '💻' },
  { value: 'Content Writing', icon: '✍️' },
  { value: 'Modelling', icon: '🧍' },
  { value: 'Studio Rental', icon: '🎙️' },
  { value: 'Brand Strategy', icon: '💡' },
  { value: 'Animation', icon: '🎞️' },
  { value: 'UI/UX Design', icon: '🖌️' },
  { value: 'SEO & Marketing', icon: '📈' },
];

const TOTAL_STEPS = 3;

export default function ClientSetupPage() {
  const navigate = useNavigate();
  const { user, updateUser } = useAuthStore();
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    location: user?.location || '',
    interests: [],
  });

  const toggleInterest = (val) => {
    setForm(f => ({
      ...f,
      interests: f.interests.includes(val)
        ? f.interests.filter(i => i !== val)
        : [...f.interests, val]
    }));
  };

  const handleFinish = async () => {
    setSaving(true);
    try {
      const { data } = await api.put('/users/me', {
        location: form.location,
      });
      updateUser(data);
    } catch {}
    navigate('/home', { replace: true });
  };

  const next = () => setStep(s => Math.min(s + 1, TOTAL_STEPS));
  const back = () => setStep(s => Math.max(s - 1, 1));
  const progress = ((step - 1) / (TOTAL_STEPS - 1)) * 100;

  return (
    <div className="min-h-screen bg-dark-bg flex flex-col px-5 pt-14 pb-10">

      {/* Progress */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-2">
          <span className="text-dark-subtext text-xs">Step {step} of {TOTAL_STEPS}</span>
          <button onClick={() => navigate('/home', { replace: true })} className="text-dark-subtext text-xs underline underline-offset-2">
            Skip for now
          </button>
        </div>
        <div className="h-1 bg-dark-surface rounded-full overflow-hidden">
          <div className="h-full rounded-full transition-all duration-500" style={{ width: `${progress}%`, background: 'linear-gradient(90deg, #6C5CE7, #a855f7)' }} />
        </div>
      </div>

      {/* Step 1: Welcome */}
      {step === 1 && (
        <div className="flex-1 flex flex-col">
          <span className="text-4xl mb-4 block">👋</span>
          <h1 className="text-2xl font-bold text-dark-text mb-2" style={{ letterSpacing: '-0.5px' }}>
            Welcome to Findr, {user?.fullName?.split(' ')[0]}!
          </h1>
          <p className="text-dark-subtext text-sm leading-relaxed mb-8">
            Findr connects you with the best creative talent in the GCC. Let's personalise your experience — it only takes a minute.
          </p>
          <div className="grid grid-cols-2 gap-3 mb-6">
            {['Find a photographer', 'Book a studio', 'Hire a designer', 'Get content made'].map(t => (
              <div key={t} className="bg-dark-surface border border-dark-border rounded-2xl px-4 py-3 text-dark-subtext text-sm">
                {t}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Step 2: Location */}
      {step === 2 && (
        <div className="flex-1 flex flex-col">
          <span className="text-4xl mb-4 block">📍</span>
          <h1 className="text-2xl font-bold text-dark-text mb-2" style={{ letterSpacing: '-0.5px' }}>
            Where are you based?
          </h1>
          <p className="text-dark-subtext text-sm mb-6">We'll show you providers near you first.</p>
          <div className="relative">
            <select
              value={form.location}
              onChange={e => setForm(f => ({ ...f, location: e.target.value }))}
              className="w-full bg-dark-surface border border-dark-border rounded-2xl px-4 py-4 text-dark-text text-base outline-none focus:border-brand-purple appearance-none"
            >
              <option value="">Select your city...</option>
              {GCC_LOCATIONS.map(g => (
                <optgroup key={g.country} label={g.country}>
                  {g.cities.map(c => <option key={c} value={c}>{c}</option>)}
                </optgroup>
              ))}
            </select>
            <svg className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6B6B7A" strokeWidth="2"><polyline points="6 9 12 15 18 9"/></svg>
          </div>
        </div>
      )}

      {/* Step 3: Interests */}
      {step === 3 && (
        <div className="flex-1 flex flex-col">
          <span className="text-4xl mb-4 block">🎯</span>
          <h1 className="text-2xl font-bold text-dark-text mb-2" style={{ letterSpacing: '-0.5px' }}>
            What are you looking for?
          </h1>
          <p className="text-dark-subtext text-sm mb-6">Pick all that interest you — we'll show you the best matches.</p>
          <div className="grid grid-cols-2 gap-2 flex-1">
            {INTEREST_OPTIONS.map(({ value, icon }) => {
              const active = form.interests.includes(value);
              return (
                <button
                  key={value}
                  onClick={() => toggleInterest(value)}
                  className={`flex items-center gap-2.5 px-4 py-3.5 rounded-2xl border text-sm font-medium text-left transition-all ${
                    active
                      ? 'border-brand-purple bg-brand-purple/10 text-dark-text'
                      : 'border-dark-border bg-dark-surface text-dark-subtext'
                  }`}
                >
                  <span className="text-xl">{icon}</span>
                  <span className="leading-tight">{value}</span>
                  {active && (
                    <span className="ml-auto w-5 h-5 rounded-full bg-brand-purple flex items-center justify-center flex-shrink-0">
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Bottom nav */}
      <div className="flex gap-3 mt-8">
        {step > 1 && (
          <button onClick={back} className="px-6 py-4 rounded-2xl bg-dark-surface border border-dark-border text-dark-text font-semibold text-sm">
            Back
          </button>
        )}
        {step < TOTAL_STEPS ? (
          <button onClick={next} className="flex-1 py-4 rounded-2xl bg-brand-gradient text-white font-bold text-base">
            Continue
          </button>
        ) : (
          <button
            onClick={handleFinish}
            disabled={saving}
            className="flex-1 py-4 rounded-2xl bg-brand-gradient text-white font-bold text-base disabled:opacity-60"
          >
            {saving ? 'Setting up...' : 'Start Exploring 🚀'}
          </button>
        )}
      </div>
    </div>
  );
}
